import { map } from "rxjs";
import { createContext } from "$lib/serv/core/local-store";
import { signInWithCustomToken } from "$lib/serv/vendor-integration/firebase";
import { extractErrorMessage } from "$lib/util/format";

export interface AdminProfile {
	id: number;
	profile: {
		email: string;
		first_name: string;
		last_name: string;
		full_name: string;
		phone_1: string;
	};
	token: string;
}

export const { getItem, observe, setItem } = createContext('mrda');
export const KEY_PROD_AUTH = 'prod-auth';
export const KEY_AUTH_TDT = 'auth-tdt';

const KEY = 'admin-profile';
const KEY_JWT = 'admin-jwt';
export const FIREBASE_CONTEXT = 'admin';

export async function login(email: string, password: string): Promise<{ success?: boolean, error?: string }> {
	const res = await fetch(`/api/admin/auth`, {
		headers: { 'Authorization': `Basic ${btoa(`${email}:${password}`)}` },
	});
	if (!res.ok) {
		let body:unknown;
		try {
			body = await res.json();
		} catch (error) {
			return { error: `${res.status} ${res.statusText}` };
		}
		return { error: extractErrorMessage(body)};
	}
	const jwt = res.headers.get('jwt');
	setItem(KEY_JWT, jwt);
	const profile = await res.json();
	setItem(KEY, profile);
	const user = await signInWithCustomToken(profile.token, FIREBASE_CONTEXT);
	// console.log('Firebase user', user)
	return { success: true };
}

export function logout () {
	setItem(KEY); // remove profile
	setItem(KEY_JWT); // remove auth
}

async function restoreFirebaseAuth() {
	let profile = getItem<AdminProfile>(KEY);
	if (!profile) return;

	const jwt = getItem<string>(KEY_JWT);
	if (!jwt) {
		logout();
		return;
	}

	const res = await fetch(`/api/admin/auth`, { headers: { 'jwt': jwt }}); // get new token
	if (!res.ok) {
		console.error('Invalid admin auth response. Signing out...');
		logout();
		return;
	}

	profile = await res.json();
	setItem(KEY, profile);
	try {
		const user = await signInWithCustomToken(profile.token, FIREBASE_CONTEXT);
		// console.log('Firebase user', user);
		console.info('Firebase authed.');
		return; // success
	} catch (error) {
		console.error('Could not sign in to Firebase. Signing out...');
	}
	logout();
}
restoreFirebaseAuth();

const profile$ = observe<AdminProfile>(KEY);

export const adminProfile$ = profile$.pipe(
	map(profile => profile.value),
	// share(), // TODO: this breaks CMS functionality?
);

export function isPossiblyAdminUser() {
	return !!getItem(KEY) && !!getItem(KEY_JWT);
}

// ############################################################
// HTTP client
// ############################################################

export function get(url:string) {
	const jwt = getItem<string>(KEY_JWT);
	return fetch(url, { headers: { 'jwt': jwt }});
}

export function post(url:string, body: object) {
	const jwt = getItem<string>(KEY_JWT);
	const method = 'POST';
	if (body instanceof FormData) {
		return fetch(url, { headers: { 'jwt': jwt }, method, body });
	}
	return fetch(url, {
		headers: { 'jwt': jwt, 'Content-Type':'application/json' },
		method: 'POST',
		body: JSON.stringify(body),
	});
}
